import React from "react";
import { Box, Card, Stack, Typography, CircularProgress, Alert, IconButton } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import { EstadoInventarioBadge, StockInventario } from "../ui";
import { INVENTARIO_ESTADO, ESTADOS_ELIMINABLES } from "../../constants";
import DateUtils from "../../../../utils/DateUtils";

/**
 * Vista móvil del listado admin de inventarios: una tarjeta equivalente a
 * cada fila de helpers/columns.js. onVer/onEditar/onEliminar reciben la fila
 * completa, misma firma que la columna de acciones del DataGrid, para
 * reutilizar los mismos handlers sin envoltorios. Mismas reglas que la
 * columna de acciones: editar solo en PENDIENTE, eliminar en ESTADOS_ELIMINABLES.
 *
 * Envuelto en React.memo: la lista de inventarios puede superar 20
 * elementos y cada tarjeta no cambia salvo que cambien sus props (rows,
 * loading o los handlers).
 */
const InventariosMobileList = React.memo(({ rows, loading, onVer, onEditar, onEliminar }) => {
    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress sx={{ color: "#cc071e" }} />
            </Box>
        );
    }

    if (!rows?.length) {
        return (
            <Alert severity="info" sx={{ mt: 2 }}>
                No hay inventarios creados todavía
            </Alert>
        );
    }

    return (
        <Stack spacing={2}>
            {rows.map((inventario) => {
                const puedeEditar = inventario.estado === INVENTARIO_ESTADO.PENDIENTE;

                return (
                    <Card key={inventario.id} sx={{ p: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
                        <Stack spacing={1.5}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#212529" }}>#{inventario.id}</Typography>
                                <EstadoInventarioBadge estado={inventario.estado} />
                            </Stack>

                            <Typography
                                sx={{ fontSize: 13, color: inventario.preparadorNombre ? "#495057" : "#6c757d" }}
                            >
                                {inventario.preparadorNombre || "Sin asignar"}
                            </Typography>

                            <Stack direction="row" spacing={2}>
                                <Typography sx={{ fontSize: 12, color: "#6c757d" }}>
                                    {inventario.lineas?.length ?? 0} artículo(s)
                                </Typography>
                                {inventario.fechaLimite && (
                                    <Typography sx={{ fontSize: 12, color: "#6c757d" }}>
                                        Límite: {DateUtils.formatearFecha(inventario.fechaLimite)}
                                    </Typography>
                                )}
                            </Stack>

                            <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
                                <StockInventario lineas={inventario.lineas} />
                                <Stack direction="row" spacing={1}>
                                <IconButton
                                    size="small"
                                    onClick={() => onVer(inventario)}
                                    aria-label="Ver inventario"
                                    sx={{ color: "#495057" }}
                                >
                                    <VisibilityIcon fontSize="small" />
                                </IconButton>
                                {puedeEditar && (
                                    <IconButton
                                        size="small"
                                        onClick={() => onEditar(inventario)}
                                        aria-label="Editar inventario"
                                        sx={{ color: "#cc071e" }}
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                )}
                                {ESTADOS_ELIMINABLES.includes(inventario.estado) && (
                                    <IconButton
                                        size="small"
                                        onClick={() => onEliminar(inventario)}
                                        aria-label="Eliminar inventario"
                                        sx={{ color: "#dc3545" }}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                )}
                                </Stack>
                            </Stack>
                        </Stack>
                    </Card>
                );
            })}
        </Stack>
    );
});

InventariosMobileList.displayName = "InventariosMobileList";

export default InventariosMobileList;
